import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import candidateService from '../../../services/candidateApi'; // Utilisation de ton service unifié
import Topbar from '../../Topbar';
import { EditIcon, DbIcon, ApiIcon } from '../theme';
import { useTheme } from '../../ThemeContext';
import { useOutletContext } from 'react-router-dom';

function DiffBadge({ diff }) {
  const { C } = useTheme();
  const hard = diff === "Hard" || diff === "HARD";
  return (
    <span style={{
      fontSize: 10.5, fontWeight: 700, padding: "3px 9px", borderRadius: 5,
      fontFamily: "'Space Mono', monospace", flexShrink: 0,
      background: hard ? "rgba(248,113,113,0.14)" : "rgba(251,191,36,0.14)",
      color: hard ? C.red : C.amber,
      border: `1px solid ${hard ? "rgba(248,113,113,0.25)" : "rgba(251,191,36,0.25)"}`,
    }}>{diff}</span>
  );
}

export default function SavedTests() {
  const { C } = useTheme();
  const navigate = useNavigate();
  const { onMenuClick } = useOutletContext();
  const testIcons = [<EditIcon />, <DbIcon />, <ApiIcon />];
  const [savedTests, setSavedTests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSavedTests = async () => {
      try {
        setLoading(true);
        // Utilisation de la méthode unifiée issue de ton candidateService
        const data = await candidateService.getSavedTests();
        if (data) {
          setSavedTests(data);
        }
      } catch (err) {
        console.error("Error fetching saved tests:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSavedTests();
  }, []);

  // Action de suppression (Appel API DELETE + mise à jour d'état synchrone)
  const handleUnsave = async (e, id) => {
    e.stopPropagation(); // Empêche la redirection vers le détail du test
    try {
      await candidateService.deleteSavedTest(id);
      // Supprime instantanément l'élément du tableau pour mettre à jour l'affichage
      setSavedTests(prev => prev.filter(test => test.id !== id));
    } catch (err) {
      console.error("Error unsaving test:", err);
    }
  };

  if (loading) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", background: C.bgDeep, color: C.textSecond }}>
        Loading saved tests...
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100vh", overflowY: "auto", background: C.bgDeep }}>
      <Topbar title="Saved Tests" onMenuClick={onMenuClick}/>
      
      <div style={{ padding: "34px 38px", display: "flex", flexDirection: "column", gap: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: C.textPrimary, marginBottom: 8, letterSpacing: "-0.5px" }}>Saved Tests</h1>
          <p style={{ fontSize: 13.5, color: C.textSecond, maxWidth: 600 }}>Your personal collection of bookmarked assessments and challenges.</p>
        </div>

        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
          {savedTests.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {savedTests.map((item, i) => (
                <div key={item.id} 
                  onClick={() => navigate(`/dashboard/test/${item.test_id || item.id}`)}
                  style={{
                    display: "flex", alignItems: "center", gap: 14,
                    padding: "16px", borderRadius: 10,
                    border: `1px solid ${C.border}`, background: C.bgItem,
                    cursor: "pointer", transition: "0.2s"
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = C.accent}
                  onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: 8, flexShrink: 0,
                    background: C.purpleDim, border: `1px solid rgba(124,58,237,0.3)`,
                    display: "flex", alignItems: "center", justifyContent: "center", color: C.accent,
                  }}>{testIcons[i % 3]}</div>
                  
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.textPrimary, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.job_name}</div>
                    <div style={{ fontSize: 12, color: C.textSecond }}>
                      {item.company_name}&nbsp;·&nbsp;{item.test_duration_min} min&nbsp;·&nbsp;
                      <span style={{ color: C.accentBright, fontWeight: 600 }}>{item.experience_level}</span>
                    </div>
                  </div>
                  
                  <DiffBadge diff={item.experience_level} />
                  
                  {/* BOUTON RETIRER DES FAVORIS (UNSAVE) */}
                  <button
                    onClick={(e) => handleUnsave(e, item.id)}
                    title="Remove from bookmarks"
                    style={{
                      marginLeft: 16, width: 34, height: 34, borderRadius: 8,
                      border: `1px solid ${C.border}`, background: "rgba(255, 255, 255, 0.02)",
                      color: C.textMuted, fontSize: 13, fontWeight: 700,
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "0.2s"
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "#ef4444"; e.currentTarget.style.color = "#ef4444"; e.currentTarget.style.background = "rgba(239, 68, 68, 0.08)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; e.currentTarget.style.background = "rgba(255, 255, 255, 0.02)"; }}
                  >
                    ✕
                  </button>

                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/dashboard/test/${item.test_id || item.id}`);
                    }}
                    style={{
                      marginLeft: 10, padding: "8px 20px", borderRadius: 8, border: "none",
                      background: C.accentDim, color: C.accentBright, fontSize: 12, fontWeight: 700,
                      cursor: "pointer", transition: "0.2s"
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = C.gradMain; e.currentTarget.style.color = "#fff"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = C.accentDim; e.currentTarget.style.color = C.accentBright; }}
                  >
                    Start Now
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "40px 0", color: C.textMuted }}>
              <p style={{ fontSize: 15, marginBottom: 8 }}>You haven't saved any tests yet.</p>
              <p style={{ fontSize: 13 }}>Browse the challenge library to build your collection.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}